import type {
  RepositoryPickerRepository$data,
  RepositoryPickerRepository$key,
} from '@github-ui/item-picker/RepositoryPickerRepository.graphql'
import {readInlineData, graphql} from 'relay-runtime'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {Button, Truncate} from '@primer/react'
import {ArrowLeftIcon} from '@primer/octicons-react'
import type {BranchPickerRef$data} from '@github-ui/item-picker/BranchPickerRef.graphql'
import {linkPullRequestsMutation} from '../../../mutations/link-pull-requests-mutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ERRORS} from '../../../constants/errors'

import {linkBranchesMutation} from '../../../mutations/link-branches'
import type {PullRequestPickerPullRequest$data} from '@github-ui/item-picker/PullRequestPickerPullRequest.graphql'
import {LazyPullRequestAndBranchPicker} from '@github-ui/item-picker/PullRequestAndBranchPicker'

import {useLazyLoadQuery, useRelayEnvironment} from 'react-relay'
import {
  RepositoryFragment,
  RepositoryPickerInternal,
  prefetchCurrentRepository,
  prefetchTopRepositories,
} from '@github-ui/item-picker/RepositoryPicker'
import type {RepositoryPickerTopRepositoriesQuery$data} from '@github-ui/item-picker/RepositoryPickerTopRepositoriesQuery.graphql'
import type {RepositoryPickerCurrentRepoQuery$data} from '@github-ui/item-picker/RepositoryPickerCurrentRepoQuery.graphql'
import type {DevelopmentPickerQuery as DevelopmentPickerQueryType} from './__generated__/DevelopmentPickerQuery.graphql'

export type LazyDevelopmentPickerBaseProp = {
  issueId: string
  linkedBranches: BranchPickerRef$data[]
  linkedPullRequests: PullRequestPickerPullRequest$data[]
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  isCreateBranchDialogOpen: boolean
  repoPickerSubtitle: React.ReactElement
  prAndBranchPickerSubtitle: React.ReactElement
  triggerOpen?: boolean
  singleKeyShortcutsEnabled: boolean
}

export type LazyDevelopmentPickerProps = LazyDevelopmentPickerBaseProp

export type LazyDevelopmentPickerInternalProps = LazyDevelopmentPickerBaseProp & {
  initialRepository: RepositoryPickerRepository$data | null
  topRepos: RepositoryPickerTopRepositoriesQuery$data | null
  /**
   * Serves as default title while preloading the other data
   */
  repoNameWithOwner?: string
}

export const DevelopmentPickerQuery = graphql`
  query DevelopmentPickerQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        repository {
          name
          nameWithOwner
          owner {
            login
          }
        }
      }
    }
  }
`

export const LazyDevelopmentPicker = ({issueId, ...rest}: LazyDevelopmentPickerProps) => {
  const {node} = useLazyLoadQuery<DevelopmentPickerQueryType>(DevelopmentPickerQuery, {id: issueId})
  const environment = useRelayEnvironment()
  const [initialRepository, setInitialRepository] = useState<RepositoryPickerRepository$data | null>(null)
  const [topRepos, setTopRepos] = useState<RepositoryPickerTopRepositoriesQuery$data | null>(null)

  useEffect(() => {
    if (!node?.repository) return

    prefetchCurrentRepository(environment, node.repository.owner.login, node.repository.name).subscribe({
      next: (data: RepositoryPickerCurrentRepoQuery$data) => {
        if (data.repository != null) {
          // eslint-disable-next-line no-restricted-syntax
          const current = readInlineData<RepositoryPickerRepository$key>(RepositoryFragment, data.repository)
          setInitialRepository(current)
        }
      },
    })
    prefetchTopRepositories(environment).subscribe({
      next: (data: RepositoryPickerTopRepositoriesQuery$data) => {
        if (data !== null) {
          setTopRepos(data)
        }
      },
    })

    // intentional to run this only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node])

  return (
    <LazyDevelopmentPickerInternal
      issueId={issueId}
      repoNameWithOwner={node?.repository?.nameWithOwner}
      initialRepository={initialRepository}
      topRepos={topRepos}
      {...rest}
    />
  )
}

export const LazyDevelopmentPickerInternal = ({
  issueId,
  initialRepository,
  topRepos,
  anchorElement,
  repoPickerSubtitle,
  prAndBranchPickerSubtitle,
  isCreateBranchDialogOpen,
  linkedBranches,
  linkedPullRequests,
  repoNameWithOwner: initialRepoNameWithOwner,
  triggerOpen = false,
  singleKeyShortcutsEnabled,
}: LazyDevelopmentPickerInternalProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [initialRepoLoading, setInitialRepoLoading] = useState(true)
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository$data | null>(null)

  const onSelectRepository = useCallback(
    (repo: RepositoryPickerRepository$data | null) => {
      if (repo !== selectedRepository) {
        setSelectedRepository(repo)
      }
      setShouldOpenPullRequestsAndBranchesPicker(true)
      setShouldOpenRepoPicker(false)
    },
    [selectedRepository],
  )

  /**
   * Persist loading until current and top repos are preloaded
   */
  useEffect(() => {
    if (initialRepoLoading && initialRepository !== null && topRepos !== null) {
      setSelectedRepository(initialRepository)
      setInitialRepoLoading(false)
    }
  }, [initialRepoLoading, initialRepository, topRepos])

  const repoNameWithOwner = useMemo(() => {
    if (selectedRepository) {
      return `${selectedRepository.owner.login}/${selectedRepository.name}`
    }

    return initialRepoNameWithOwner || ''
  }, [initialRepoNameWithOwner, selectedRepository])

  const [shouldOpenPullRequestsAndBranchesPicker, setShouldOpenPullRequestsAndBranchesPicker] =
    useState<boolean>(triggerOpen)

  const [shouldOpenRepoPicker, setShouldOpenRepoPicker] = useState<boolean>(false)

  const linkedPullRequestsForRepo = useMemo(() => {
    return linkedPullRequests.filter(pr => pr.repository.id === selectedRepository?.id)
  }, [linkedPullRequests, selectedRepository?.id])

  const linkedBranchesForRepo = useMemo(() => {
    return linkedBranches.filter(branch => branch.repository.id === selectedRepository?.id)
  }, [linkedBranches, selectedRepository?.id])

  const handleClickRepoPicker = useCallback(() => {
    onSelectRepository(null)
    setShouldOpenRepoPicker(true)
  }, [onSelectRepository])

  //  listen to shortcuts here dooh
  const pullRequestAndBranchPickerHeadingRepoNav = useMemo(() => {
    return (
      <>
        <Button
          variant="invisible"
          leadingVisual={ArrowLeftIcon}
          size="small"
          title={'Back to repositories'}
          onClick={handleClickRepoPicker}
          sx={{
            color: 'fg.default',
            px: 2,
            '> [data-component="text"]': {
              overflow: 'hidden',
            }, // needed to truncate button text
          }}
        >
          <Truncate id="repo-name" title={repoNameWithOwner} sx={{fontSize: 2, maxWidth: 250}}>
            {repoNameWithOwner}
          </Truncate>
        </Button>
      </>
    )
  }, [handleClickRepoPicker, repoNameWithOwner])

  const onSelectionChange = useCallback(
    (selected: Array<PullRequestPickerPullRequest$data | BranchPickerRef$data>) => {
      const newItems = selected.filter(
        item =>
          [...linkedPullRequestsForRepo, ...linkedBranchesForRepo].find(prOrBranch => prOrBranch.id === item.id) ===
          undefined,
      )
      const existingItems = [...linkedPullRequests, ...linkedBranches].filter(
        item =>
          item.repository.id !== selectedRepository?.id ||
          selected.find(prOrBranch => prOrBranch.id === item.id) !== undefined,
      )

      const sortedItems = [...existingItems, ...newItems]
      const sortedPrs = sortedItems.filter(
        item => item.__typename === 'PullRequest',
      ) as PullRequestPickerPullRequest$data[]
      const sortedBranches = sortedItems.filter(item => item.__typename === 'Ref') as BranchPickerRef$data[]

      linkPullRequestsMutation({
        environment,
        input: {
          baseIssueOrPullRequestId: `${issueId}`,
          linkingIds: sortedPrs.map(pr => pr.id),
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedPullRequests,
          })
        },
        linkingPrs: sortedPrs,
      })

      linkBranchesMutation({
        environment,
        input: {
          baseIssueId: `${issueId}`,
          linkingIds: sortedBranches.map(branch => branch.id),
        },
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateLinkedBranches,
          })
        },
      })
    },
    [
      addToast,
      environment,
      issueId,
      linkedBranches,
      linkedBranchesForRepo,
      linkedPullRequests,
      linkedPullRequestsForRepo,
      selectedRepository?.id,
    ],
  )

  return (
    <>
      {!initialRepoLoading && !selectedRepository && (
        <RepositoryPickerInternal
          initialRepository={undefined}
          topRepositoriesData={topRepos?.viewer || null}
          onSelect={repo => onSelectRepository(repo ?? null)}
          anchorElement={anchorElement}
          title={'Link a branch or pull request'}
          subtitle={repoPickerSubtitle}
          preventClose={isCreateBranchDialogOpen}
          preventDefault={true}
          triggerOpen={shouldOpenRepoPicker}
        />
      )}
      {(initialRepoLoading || selectedRepository) && (
        <LazyPullRequestAndBranchPicker
          repoNameWithOwner={repoNameWithOwner}
          initialSelectedPrs={linkedPullRequestsForRepo}
          initialSelectedBranches={linkedBranchesForRepo}
          onSelectionChange={onSelectionChange}
          anchorElement={anchorElement}
          title={pullRequestAndBranchPickerHeadingRepoNav}
          subtitle={prAndBranchPickerSubtitle}
          shortcutsEnabled={singleKeyShortcutsEnabled}
          triggerOpen={shouldOpenPullRequestsAndBranchesPicker}
          preventClose={isCreateBranchDialogOpen}
        />
      )}
    </>
  )
}

try{ LazyDevelopmentPicker.displayName ||= 'LazyDevelopmentPicker' } catch {}
try{ LazyDevelopmentPickerInternal.displayName ||= 'LazyDevelopmentPickerInternal' } catch {}